import {
  Award,
  BookOpen,
  ClipboardCheck,
  Coins,
  FileWarning,
  Flag,
  GraduationCap,
  Megaphone,
  MessageCircle,
  ShoppingCart,
  UserMinus,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

/**
 * Shared notification helpers. Deliberately component-FREE: eslint's
 * `react-refresh/only-export-components` warns once per non-component export
 * in a file that also exports components, and `packages/ui` sits at 9 of its
 * 10 allowed warnings. Keeping the pure helpers here costs nothing and keeps
 * the budget intact.
 */

/** Wire shape of `GET /me/notifications` — identical in all three SPAs. */
export type NotificationItem = {
  id: string;
  kind: string;
  title: string;
  body: string;
  metadata: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
};

/**
 * The deep link a row navigates to, or null.
 *
 * Only SPA-RELATIVE paths are accepted: a leading `/` and not `//`. Every
 * `path` is written by our own server code, so this is defence in depth
 * rather than a live threat — but a notification row is user-visible content,
 * and a bell that would follow `https://…` or `//evil.example` out of the app
 * is one bad metadata write away from being an open redirect.
 */
export function notificationPath(item: Pick<NotificationItem, 'metadata'>): string | null {
  const raw = item.metadata?.path;
  if (typeof raw !== 'string') return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  return raw;
}

/** "30s ago" / "5m ago" / "3h ago" / "2d ago", then an absolute date. */
export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export type NotificationTone = 'primary' | 'success' | 'warning' | 'muted';

/**
 * kind → icon, keyed by EXACT code. Never prefix-matched: `creator_payout_paid`
 * and `creator_approved` share a prefix but belong to different families, and
 * a `startsWith` map would silently send every payout bell to the wrong glyph.
 * Kinds that predate LMS Phase 2 keep the shopping-cart default they render
 * with today, so nothing about the member bell changes visually.
 */
export const NOTIFICATION_ICONS: Record<string, { Icon: LucideIcon; tone: NotificationTone }> = {
  // learner
  course_enrolled: { Icon: BookOpen, tone: 'primary' },
  learning_reminder: { Icon: BookOpen, tone: 'primary' },
  question_answered: { Icon: MessageCircle, tone: 'primary' },
  test_passed: { Icon: ClipboardCheck, tone: 'success' },
  test_failed: { Icon: ClipboardCheck, tone: 'warning' },
  certificate_issued: { Icon: Award, tone: 'success' },
  course_announcement: { Icon: Megaphone, tone: 'primary' },
  // creator
  creator_question: { Icon: MessageCircle, tone: 'primary' },
  creator_grading: { Icon: ClipboardCheck, tone: 'primary' },
  creator_approved: { Icon: GraduationCap, tone: 'success' },
  creator_rejected: { Icon: GraduationCap, tone: 'warning' },
  creator_suspended: { Icon: GraduationCap, tone: 'warning' },
  course_approved: { Icon: GraduationCap, tone: 'success' },
  course_rejected: { Icon: GraduationCap, tone: 'warning' },
  course_content_approved: { Icon: FileWarning, tone: 'success' },
  course_content_rejected: { Icon: FileWarning, tone: 'warning' },
  creator_sale: { Icon: Coins, tone: 'success' },
  creator_payout_paid: { Icon: Wallet, tone: 'success' },
  creator_payout_rejected: { Icon: Wallet, tone: 'warning' },
  // stairstep
  rank_up: { Icon: Award, tone: 'success' },
  // owner alerts
  creator_applied: { Icon: GraduationCap, tone: 'primary' },
  creator_course_submitted: { Icon: GraduationCap, tone: 'primary' },
  creator_payout_requested: { Icon: Wallet, tone: 'primary' },
  house_grading_pending: { Icon: ClipboardCheck, tone: 'primary' },
  creator_course_content_changed: { Icon: FileWarning, tone: 'warning' },
  content_reported: { Icon: Flag, tone: 'warning' },
  // Not an LMS kind (no `lms_enabled` gate) — a member asking to be erased is
  // an account-lifecycle alert, so it rides the same hardcoded-copy convention
  // as `feedback` and `kyc_submitted`.
  account_archive_requested: { Icon: UserMinus, tone: 'warning' },
  accounts_blocked_inactivity: { Icon: UserMinus, tone: 'warning' },
};

export const NOTIFICATION_TONE_CLASSES: Record<NotificationTone, string> = {
  primary: 'bg-primary/10 text-primary',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  muted: 'bg-muted text-muted-foreground',
};

/** Icon + tone for a row. `earnings` keeps its cash-vs-scrip colouring. */
export function notificationVisual(
  kind: string,
  metadata?: Record<string, unknown>,
): { Icon: LucideIcon; tone: NotificationTone } {
  if (kind === 'earnings') {
    const cash = String(metadata?.currency ?? 'cash') === 'cash';
    return { Icon: Coins, tone: cash ? 'success' : 'primary' };
  }
  return NOTIFICATION_ICONS[kind] ?? { Icon: ShoppingCart, tone: 'muted' };
}
